import React from 'react';
import {
  DollarSign, TrendingUp, Package, CreditCard, BarChart3, Award,
} from 'lucide-react';
import { getCostForItem, formatCurrency, getLicenseStatus, formatCPF, formatCNPJ } from './adminHelpers';

const AdminDashboard = ({ orders, costConfig }) => {
  const approved  = orders.filter(o => o.payment_status === 'approved');
  const pending   = orders.filter(o => !o.payment_status || o.payment_status === 'pending');
  const licensed  = orders.filter(o => o.payment_status === 'license_generated');
  const cancelled = orders.filter(o => ['rejected', 'cancelled', 'refunded'].includes(o.payment_status));

  const revenue   = [...approved, ...licensed].reduce((s, o) => s + (o.amount_paid || o.total || 0), 0);
  const totalCost = [...approved, ...licensed].reduce((s, o) =>
    s + (o.items || []).reduce((s2, i) => s2 + getCostForItem(i.name, costConfig) * (i.quantity || 1), 0), 0);
  const profit = revenue - totalCost;

  const now = new Date();
  const activeLicenses  = licensed.filter(o => o.license_expires_at && new Date(o.license_expires_at) > now);
  const expiredLicenses = licensed.filter(o => o.license_expires_at && new Date(o.license_expires_at) <= now);
  const expiringIn30    = licensed.filter(o => {
    if (!o.license_expires_at) return false;
    const diff = Math.ceil((new Date(o.license_expires_at) - now) / (1000 * 60 * 60 * 24));
    return diff > 0 && diff <= 30;
  });

  const productSales = {};
  [...approved, ...licensed].forEach(o => {
    (o.items || []).forEach(item => {
      if (!productSales[item.name]) productSales[item.name] = { qty: 0, revenue: 0, cost: 0 };
      productSales[item.name].qty     += item.quantity || 1;
      productSales[item.name].revenue += item.price * (item.quantity || 1);
      productSales[item.name].cost    += getCostForItem(item.name, costConfig) * (item.quantity || 1);
    });
  });
  const maxQty = Math.max(...Object.values(productSales).map(p => p.qty), 1);

  return (
    <div className="p-4 space-y-4 overflow-y-auto">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Faturamento Total', value: formatCurrency(revenue), icon: DollarSign, color: 'cyan', sub: `${approved.length + licensed.length} vendas concluídas` },
          { label: 'Lucro Líquido', value: formatCurrency(profit), icon: TrendingUp, color: 'green', sub: `Margem ${revenue > 0 ? ((profit / revenue) * 100).toFixed(1) : 0}%` },
          { label: 'Custo Total', value: formatCurrency(totalCost), icon: Package, color: 'orange', sub: 'Licenças adquiridas' },
          { label: 'Ticket Médio', value: formatCurrency(revenue / Math.max(approved.length + licensed.length, 1)), icon: CreditCard, color: 'purple', sub: 'Por venda' },
        ].map((kpi, i) => (
          <div key={i} className={`bg-gradient-to-br from-${kpi.color}-500/10 to-slate-800/50 border border-${kpi.color}-500/30 rounded-xl p-4`}>
            <div className={`flex items-center gap-2 text-${kpi.color}-400 text-xs mb-2`}><kpi.icon className="w-4 h-4" />{kpi.label}</div>
            <div className="text-xl font-bold text-white">{kpi.value}</div>
            <div className="text-xs text-slate-500 mt-1">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* License Tracking */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-blue-400" />Controle de Licenças (validade 12 meses)</h3>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { count: activeLicenses.length,  label: 'Ativas',                color: 'green'  },
            { count: expiringIn30.length,     label: 'Expirando em 30 dias',  color: 'yellow' },
            { count: expiredLicenses.length,  label: 'Expiradas',             color: 'red'    },
          ].map((s, i) => (
            <div key={i} className={`bg-${s.color}-500/10 border border-${s.color}-500/30 rounded-lg p-3 text-center`}>
              <div className={`text-2xl font-bold text-${s.color}-400`}>{s.count}</div>
              <div className="text-xs text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>
        {licensed.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {licensed
              .sort((a, b) => new Date(a.license_expires_at || 0) - new Date(b.license_expires_at || 0))
              .map((o, i) => {
                const c    = o.customer || {};
                const info = getLicenseStatus(o);
                const exp  = o.license_expires_at ? new Date(o.license_expires_at).toLocaleDateString('pt-BR') : '-';
                return (
                  <div key={i} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-2.5 border border-slate-700">
                    <div className="flex-1 min-w-0">
                      <span className="text-white text-sm font-medium">{c.nome_completo || c.razao_social || o.customer_name || '-'}</span>
                      <span className="text-xs text-slate-500 ml-2">{c.cpf ? formatCPF(c.cpf) : c.cnpj ? formatCNPJ(c.cnpj) : ''}</span>
                      <div className="text-xs text-slate-500">{(o.items || []).map(i => i.name).join(', ')}</div>
                    </div>
                    <div className="text-right ml-3 shrink-0">
                      <div className={`text-xs font-medium text-${info?.color || 'slate'}-400`}>{info?.expired ? 'EXPIRADA' : info?.label || '-'}</div>
                      <div className="text-xs text-slate-500">{exp}</div>
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <p className="text-slate-500 text-sm text-center py-2">Nenhuma licença gerada ainda</p>
        )}
      </div>

      {/* Status + Product charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-cyan-400" />Status dos Pedidos</h3>
          <div className="space-y-3">
            {[
              { label: 'Aprovados',              count: approved.length,  color: 'green'  },
              { label: 'Licença Gerada',          count: licensed.length,  color: 'blue'   },
              { label: 'Pendentes',               count: pending.length,   color: 'yellow' },
              { label: 'Cancelados/Rejeitados',   count: cancelled.length, color: 'red'    },
            ].map((s, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className={`text-${s.color}-400`}>{s.label}</span>
                  <span className="text-white font-bold">{s.count}</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className={`h-full bg-${s.color}-500 rounded-full`}
                    style={{ width: `${orders.length > 0 ? (s.count / orders.length) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-cyan-400" />Vendas por Produto</h3>
          {Object.keys(productSales).length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">Nenhuma venda ainda</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(productSales).sort((a, b) => b[1].qty - a[1].qty).map(([name, data], i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300 truncate mr-2">{name}</span>
                    <span className="text-white font-bold whitespace-nowrap">{data.qty}x — {formatCurrency(data.revenue)}</span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${(data.qty / maxQty) * 100}%` }} />
                  </div>
                  <div className="text-xs text-green-400 mt-0.5">Lucro: {formatCurrency(data.revenue - data.cost)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
